
import type React from "react"

import { useState, useRef } from "react"
import { FileSpreadsheet, Upload } from "lucide-react"
import { useAnalyzeExcel, useDownloadTemplate } from "../../hooks/useProjectImport"

interface UploadStepProps {
  onSuccess: (file: File, headers: string[], rows: string[][]) => void
  onError: (message: string) => void
}

export default function UploadStep({ onSuccess, onError }: UploadStepProps) {
  const [file, setFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const downloadTemplate = useDownloadTemplate()
  const analyzeExcel = useAnalyzeExcel()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      onError("")
    }
  }

  const handleAnalyze = async () => {
    if (!file) return

    try {
      const result = await analyzeExcel.mutateAsync(file)
      onSuccess(file, result.sampleHeaders, result.sampleRows)
    } catch (error) {
      onError("Failed to analyze Excel file. Please check the format and try again.")
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium mb-2">Upload Project Excel File</h3>
        <p className="text-gray-500 mb-4">
          Upload your Excel file containing project data to begin the import process.
        </p>
      </div>

      <div
        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => fileInputRef.current?.click()}
      >
        {file ? (
          <>
            <FileSpreadsheet className="h-12 w-12 text-green-500 mx-auto mb-2" />
            <p className="text-lg font-medium">{file.name}</p>
            <p className="text-sm text-gray-500 mt-1">{(file.size / 1024).toFixed(2)} KB</p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setFile(null)
                if (fileInputRef.current) {
                  fileInputRef.current.value = ""
                }
              }}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              Change File
            </button>
          </>
        ) : (
          <>
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-lg font-medium">Click to upload or drag and drop</p>
            <p className="text-sm text-gray-500 mt-1">Excel files only (.xlsx)</p>
          </>
        )}
        <input type="file" ref={fileInputRef} className="hidden" accept=".xlsx" onChange={handleFileChange} />
      </div>

      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={() => downloadTemplate.mutate()}
          disabled={downloadTemplate.isPending}
          className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          {downloadTemplate.isPending ? (
            <span className="flex items-center">
              <svg
                className="mr-2 h-4 w-4 text-gray-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Downloading...
            </span>
          ) : (
            "Download Template"
          )}
        </button>
        <button
          type="button"
          onClick={handleAnalyze}
          disabled={!file || analyzeExcel.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {analyzeExcel.isPending ? (
            <span className="flex items-center">
              <svg
                className="mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Analyzing...
            </span>
          ) : (
            "Analyze File"
          )}
        </button>
      </div>
    </div>
  )
}
